import React, { useEffect, useMemo, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import {
    Add as AddIcon,
    Archive as ArchiveIcon,
    Edit as EditIcon,
    Preview as PreviewIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import AgentDashboardHome from '../AgentDashboardHome';
import AgentPolicyContent from '../common/AgentPolicyContent';

const BADGE_COLORS = ['default', 'primary', 'secondary', 'success', 'warning', 'info', 'error'];
const STATUSES = ['active', 'scheduled', 'expired', 'archived'];
const SECTION_TITLES = [
    'Primary Quick Links',
    'EPP Tools',
    'Store Info',
    'Sales Tools',
    'Training Shortcuts',
    'Backup Tools'
];

function textValue(value) {
    return String(value || '').trim();
}

function formatDateTimeLocal(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60000);
    return localDate.toISOString().slice(0, 16);
}

function normalizeDateInput(value) {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function sortItems(items = []) {
    return [...items].sort((a, b) => {
        if (Boolean(a.pinned) !== Boolean(b.pinned)) {
            return a.pinned ? -1 : 1;
        }
        return Number(a.sortOrder || 999) - Number(b.sortOrder || 999);
    });
}

function isLiveItem(item, now = new Date()) {
    if (!item || item.visibleToAgents === false || item.status === 'archived' || item.status === 'expired') {
        return false;
    }
    const startsAt = item.startsAt ? new Date(item.startsAt) : null;
    const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
    if (startsAt && !Number.isNaN(startsAt.getTime()) && startsAt > now) return false;
    if (expiresAt && !Number.isNaN(expiresAt.getTime()) && expiresAt < now) return false;
    return true;
}

function buildPreviewDashboard(config) {
    const now = new Date();
    const sections = [...(config?.sections || [])]
        .filter((section) => section.visible !== false)
        .sort((a, b) => Number(a.sortOrder || 999) - Number(b.sortOrder || 999));

    return {
        generatedAt: now.toISOString(),
        sections,
        announcements: sortItems(config?.announcements || [])
            .filter((item) => isLiveItem(item, now))
            .map((item) => ({
                id: item.id,
                title: item.title,
                description: item.description,
                badgeText: item.badgeText,
                badgeColor: item.badgeColor,
                ctaText: item.ctaText,
                url: item.url,
                internalRoute: item.internalRoute,
                openInNewTab: item.openInNewTab,
                pinned: item.pinned,
                sortOrder: item.sortOrder
            })),
        quickLinks: sortItems(config?.quickLinks || [])
            .filter((item) => isLiveItem(item, now))
            .map((item) => ({
                id: item.id,
                label: item.label,
                description: item.description,
                url: item.url,
                internalRoute: item.internalRoute,
                openInNewTab: item.openInNewTab,
                pinned: item.pinned,
                primaryAction: item.primaryAction,
                sortOrder: item.sortOrder,
                section: item.section,
                iconType: item.iconType
            })),
        storeInfo: config?.storeInfo?.visibleToAgents === false ? null : config?.storeInfo
    };
}

function makeId(prefix, title) {
    const slug = textValue(title)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
        .slice(0, 40);
    return `${prefix}-${slug || Date.now()}-${Date.now()}`;
}

function formatPolicyTimestamp(value) {
    if (!value) return 'Not published';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
}

function DashboardItemCard({ item, type, onEdit, onToggleVisible, onArchive }) {
    const title = type === 'announcement' ? item.title : item.label;
    const detail = item.description;
    const badgeText = type === 'announcement' ? item.badgeText : item.section;

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={2}>
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                            <Typography variant="h6" fontWeight={700}>
                                {title || 'Untitled'}
                            </Typography>
                            {badgeText && (
                                <Chip
                                    label={badgeText}
                                    color={type === 'announcement' ? item.badgeColor || 'default' : 'default'}
                                    size="small"
                                />
                            )}
                            <Chip label={item.status || 'active'} size="small" variant="outlined" />
                            {item.pinned && <Chip label="Pinned" size="small" color="primary" variant="outlined" />}
                            {item.visibleToAgents === false && <Chip label="Hidden" size="small" color="warning" variant="outlined" />}
                        </Stack>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            {detail}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {item.url || 'No link'} | Sort {item.sortOrder ?? 0}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                        <IconButton aria-label="Edit" onClick={() => onEdit(item)}>
                            <EditIcon />
                        </IconButton>
                        <IconButton aria-label={item.visibleToAgents === false ? 'Show' : 'Hide'} onClick={() => onToggleVisible(item)}>
                            {item.visibleToAgents === false ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                        <IconButton aria-label="Archive" onClick={() => onArchive(item)} color="warning">
                            <ArchiveIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}

function ItemEditDialog({ open, type, item, onClose, onSave }) {
    const [draft, setDraft] = useState(item || {});

    useEffect(() => {
        setDraft(item || {});
    }, [item]);

    const update = (field, value) => {
        setDraft((previous) => ({ ...previous, [field]: value }));
    };

    const titleLabel = type === 'announcement' ? 'Title' : 'Label';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{item?.id ? 'Edit' : 'Add'} {type === 'announcement' ? 'Announcement' : 'Quick Link'}</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <TextField
                        label={titleLabel}
                        value={type === 'announcement' ? draft.title || '' : draft.label || ''}
                        onChange={(event) => update(type === 'announcement' ? 'title' : 'label', event.target.value)}
                        fullWidth
                        required
                    />
                    <TextField
                        label="Description"
                        value={draft.description || ''}
                        onChange={(event) => update('description', event.target.value)}
                        fullWidth
                        multiline
                        minRows={3}
                    />

                    {type === 'announcement' && (
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="Badge text"
                                    value={draft.badgeText || ''}
                                    onChange={(event) => update('badgeText', event.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth>
                                    <InputLabel>Badge color</InputLabel>
                                    <Select
                                        label="Badge color"
                                        value={draft.badgeColor || 'default'}
                                        onChange={(event) => update('badgeColor', event.target.value)}
                                    >
                                        {BADGE_COLORS.map((color) => (
                                            <MenuItem key={color} value={color}>{color}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label="CTA text"
                                    value={draft.ctaText || ''}
                                    onChange={(event) => update('ctaText', event.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={type === 'quick_link' ? 6 : 12}>
                            <TextField
                                label="Link URL"
                                value={draft.url || ''}
                                onChange={(event) => update('url', event.target.value)}
                                fullWidth
                            />
                        </Grid>
                        {type === 'quick_link' && (
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel>Section</InputLabel>
                                    <Select
                                        label="Section"
                                        value={draft.section || 'Primary Quick Links'}
                                        onChange={(event) => update('section', event.target.value)}
                                    >
                                        {SECTION_TITLES.map((section) => (
                                            <MenuItem key={section} value={section}>{section}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        )}
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Sort order"
                                type="number"
                                value={draft.sortOrder ?? 10}
                                onChange={(event) => update('sortOrder', Number(event.target.value))}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select label="Status" value={draft.status || 'active'} onChange={(event) => update('status', event.target.value)}>
                                    {STATUSES.map((status) => (
                                        <MenuItem key={status} value={status}>{status}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Start date/time"
                                type="datetime-local"
                                value={formatDateTimeLocal(draft.startsAt)}
                                onChange={(event) => update('startsAt', normalizeDateInput(event.target.value))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Expiry date/time"
                                type="datetime-local"
                                value={formatDateTimeLocal(draft.expiresAt)}
                                onChange={(event) => update('expiresAt', normalizeDateInput(event.target.value))}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <FormControlLabel control={<Switch checked={draft.visibleToAgents !== false} onChange={(event) => update('visibleToAgents', event.target.checked)} />} label="Visible to agents" />
                        <FormControlLabel control={<Switch checked={Boolean(draft.pinned)} onChange={(event) => update('pinned', event.target.checked)} />} label="Pinned" />
                        <FormControlLabel control={<Switch checked={Boolean(draft.internalRoute)} onChange={(event) => update('internalRoute', event.target.checked)} />} label="Internal route" />
                        <FormControlLabel control={<Switch checked={draft.openInNewTab !== false} onChange={(event) => update('openInNewTab', event.target.checked)} />} label="Open in new tab" />
                        {type === 'quick_link' && (
                            <FormControlLabel control={<Switch checked={Boolean(draft.primaryAction)} onChange={(event) => update('primaryAction', event.target.checked)} />} label="Primary action" />
                        )}
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={() => onSave(draft)}
                >
                    Save Item
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function AgentDashboardManager() {
    const [config, setConfig] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('announcements');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validation, setValidation] = useState(null);
    const [editState, setEditState] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [policyAdmin, setPolicyAdmin] = useState(null);
    const [policyDraft, setPolicyDraft] = useState({ title: 'Company Policy & Work Rules', html: '' });
    const [policyLoading, setPolicyLoading] = useState(true);
    const [policySaving, setPolicySaving] = useState(false);
    const [policyMessage, setPolicyMessage] = useState('');
    const [policyError, setPolicyError] = useState('');
    const [policyPreviewOpen, setPolicyPreviewOpen] = useState(false);

    const previewDashboard = useMemo(() => config ? buildPreviewDashboard(config) : null, [config]);
    const previewPolicyStatus = useMemo(() => {
        const policy = policyAdmin?.policy;
        if (!policy?.enabled || !policy?.activeVersion || !policy?.activeHtml) {
            return null;
        }

        return {
            enabled: true,
            activeVersion: policy.activeVersion,
            requiresAcknowledgement: false,
            acknowledged: false,
            exempt: true,
            acknowledgement: null,
            activePolicy: {
                version: policy.activeVersion,
                title: policy.activeTitle || 'Company Policy & Work Rules',
                html: policy.activeHtml,
                publishedAt: policy.publishedAt || null
            }
        };
    }, [policyAdmin]);

    const loadConfig = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await fetch('/api/agent-dashboard/config');
            const data = await response.json();
            if (data.success) {
                setConfig(data.config);
                setMeta(data.meta);
            } else {
                setError(data.error || 'Failed to load dashboard manager.');
            }
        } catch (loadError) {
            setError('Failed to connect to dashboard manager service.');
        } finally {
            setLoading(false);
        }
    };

    const loadPolicyAdmin = async () => {
        setPolicyLoading(true);
        setPolicyError('');
        try {
            const response = await fetch('/api/agent-dashboard/policy/admin');
            const data = await response.json();
            if (data.success) {
                setPolicyAdmin({ policy: data.policy, report: data.report });
                setPolicyDraft({
                    title: data.policy?.draftTitle || 'Company Policy & Work Rules',
                    html: data.policy?.draftHtml || ''
                });
            } else {
                setPolicyError(data.error || 'Failed to load policy manager.');
            }
        } catch (loadError) {
            setPolicyError('Failed to connect to policy manager service.');
        } finally {
            setPolicyLoading(false);
        }
    };

    useEffect(() => {
        loadConfig();
        loadPolicyAdmin();
    }, []);

    const setCollection = (collection, items) => {
        setConfig((previous) => ({
            ...previous,
            [collection]: items
        }));
        setSuccess('');
        setValidation(null);
    };

    const upsertItem = (collection, item) => {
        const items = config?.[collection] || [];
        const withId = {
            ...item,
            id: item.id || makeId(collection === 'announcements' ? 'announcement' : 'quick-link', item.title || item.label),
            type: collection === 'announcements' ? 'announcement' : 'quick_link'
        };
        const exists = items.some((existing) => existing.id === withId.id);
        const nextItems = exists
            ? items.map((existing) => existing.id === withId.id ? withId : existing)
            : [...items, withId];
        setCollection(collection, nextItems);
        setEditState(null);
    };

    const updateItem = (collection, target, updates) => {
        setCollection(collection, (config?.[collection] || []).map((item) => (
            item.id === target.id ? { ...item, ...updates } : item
        )));
    };

    const validateConfig = async () => {
        setError('');
        setSuccess('');
        try {
            const response = await fetch('/api/agent-dashboard/validate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ config })
            });
            const data = await response.json();
            setValidation(data);
            if (data.valid) {
                setSuccess('Dashboard config is valid.');
            } else {
                setError('Dashboard config needs attention before saving.');
            }
            return data;
        } catch (validateError) {
            setError('Failed to validate dashboard config.');
            return { valid: false };
        }
    };

    const saveConfig = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const validationResult = await validateConfig();
            if (!validationResult.valid) {
                return;
            }
            const response = await fetch('/api/agent-dashboard/config', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    config,
                    changeSummary: 'Agent dashboard manager save.'
                })
            });
            const data = await response.json();
            if (data.success) {
                setConfig(data.config);
                setSuccess('Agent dashboard saved. Agents will see the active visible items immediately.');
                await loadConfig();
            } else {
                setValidation(data);
                setError(data.error || 'Failed to save dashboard config.');
            }
        } catch (saveError) {
            setError('Failed to save dashboard config.');
        } finally {
            setSaving(false);
        }
    };

    const savePolicyDraft = async () => {
        setPolicySaving(true);
        setPolicyError('');
        setPolicyMessage('');
        try {
            const response = await fetch('/api/agent-dashboard/policy/draft', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ policy: policyDraft })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to save policy draft.');
            }
            setPolicyAdmin({ policy: data.policy, report: data.report });
            setPolicyDraft({
                title: data.policy?.draftTitle || policyDraft.title,
                html: data.policy?.draftHtml || policyDraft.html
            });
            setPolicyMessage('Policy draft saved. Agents are not blocked until a new required version is published.');
        } catch (saveError) {
            setPolicyError(saveError.message || 'Failed to save policy draft.');
        } finally {
            setPolicySaving(false);
        }
    };

    const publishPolicy = async () => {
        const confirmed = window.confirm('Publish this draft as a new required policy version? Active agents will need to acknowledge it.');
        if (!confirmed) return;
        setPolicySaving(true);
        setPolicyError('');
        setPolicyMessage('');
        try {
            const response = await fetch('/api/agent-dashboard/policy/publish', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to publish policy.');
            }
            setPolicyAdmin({ policy: data.policy, report: data.report });
            setPolicyDraft({
                title: data.policy?.draftTitle || policyDraft.title,
                html: data.policy?.draftHtml || policyDraft.html
            });
            setPolicyMessage(`Published policy version ${data.policy?.activeVersion || ''}. Active agents will be required to acknowledge it.`);
        } catch (publishError) {
            setPolicyError(publishError.message || 'Failed to publish policy.');
        } finally {
            setPolicySaving(false);
        }
    };

    const disablePolicy = async () => {
        const confirmed = window.confirm('Disable the current required policy acknowledgement? Agents will no longer be blocked by this policy version.');
        if (!confirmed) return;
        setPolicySaving(true);
        setPolicyError('');
        setPolicyMessage('');
        try {
            const response = await fetch('/api/agent-dashboard/policy/disable', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.error || 'Failed to disable policy requirement.');
            }
            setPolicyAdmin({ policy: data.policy, report: data.report });
            setPolicyMessage('Policy acknowledgement requirement disabled. Draft content remains available for editing.');
        } catch (disableError) {
            setPolicyError(disableError.message || 'Failed to disable policy requirement.');
        } finally {
            setPolicySaving(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
            </Box>
        );
    }

    if (!config) {
        return <Alert severity="error">{error || 'Dashboard manager config is unavailable.'}</Alert>;
    }

    const tabs = [
        { id: 'announcements', label: 'Announcements' },
        { id: 'quickLinks', label: 'Quick Links' },
        { id: 'storeInfo', label: 'Store Info' },
        { id: 'sections', label: 'Sections' },
        { id: 'policy', label: 'Policy' }
    ];

    return (
        <Box>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={3}>
                <Box>
                    <Typography variant="h4" fontWeight={700} gutterBottom>
                        Agent Dashboard Manager
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Control the agent home page: announcements, one-day deals, quick links, and store info.
                    </Typography>
                    {meta?.updatedAt && (
                        <Typography variant="caption" color="text.secondary">
                            Last saved: {meta.updatedAt}
                        </Typography>
                    )}
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                    <Button variant="outlined" startIcon={<PreviewIcon />} onClick={() => setPreviewOpen(true)}>
                        Preview Agent Dashboard
                    </Button>
                    <Button variant="outlined" onClick={validateConfig}>
                        Validate
                    </Button>
                    <Button variant="contained" startIcon={<SaveIcon />} onClick={saveConfig} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Active Dashboard'}
                    </Button>
                </Stack>
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
                Admin controls only. Agents see active, visible, non-expired dashboard items. This page does not send emails or change Bundle Builder behavior.
            </Alert>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            {validation?.errors?.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Validation issues</Typography>
                    <Box component="ul" sx={{ mt: 1, mb: 0 }}>
                        {validation.errors.slice(0, 6).map((item) => <li key={item}>{item}</li>)}
                    </Box>
                </Alert>
            )}

            <Paper sx={{ mb: 3 }}>
                <Tabs value={tab} onChange={(event, nextTab) => setTab(nextTab)} variant="scrollable" scrollButtons="auto">
                    {tabs.map((tabItem) => (
                        <Tab key={tabItem.id} value={tabItem.id} label={tabItem.label} />
                    ))}
                </Tabs>
            </Paper>

            {tab === 'announcements' && (
                <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Announcements / One-Day Deals / Hot Updates</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setEditState({ type: 'announcement', item: { visibleToAgents: true, status: 'active', badgeColor: 'primary', sortOrder: 10 } })}
                        >
                            Add Announcement
                        </Button>
                    </Box>
                    {sortItems(config.announcements).map((item) => (
                        <DashboardItemCard
                            key={item.id}
                            item={item}
                            type="announcement"
                            onEdit={(target) => setEditState({ type: 'announcement', item: target })}
                            onToggleVisible={(target) => updateItem('announcements', target, { visibleToAgents: target.visibleToAgents === false })}
                            onArchive={(target) => updateItem('announcements', target, { status: 'archived', visibleToAgents: false })}
                        />
                    ))}
                </Stack>
            )}

            {tab === 'quickLinks' && (
                <Stack spacing={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Quick Links</Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setEditState({ type: 'quick_link', item: { visibleToAgents: true, status: 'active', section: 'Primary Quick Links', sortOrder: 10 } })}
                        >
                            Add Quick Link
                        </Button>
                    </Box>
                    {SECTION_TITLES.map((section) => {
                        const links = sortItems((config.quickLinks || []).filter((item) => item.section === section));
                        if (links.length === 0) return null;
                        return (
                            <Paper key={section} sx={{ p: 2 }}>
                                <Typography variant="subtitle1" color="primary" gutterBottom>
                                    {section}
                                </Typography>
                                <Stack spacing={2}>
                                    {links.map((item) => (
                                        <DashboardItemCard
                                            key={item.id}
                                            item={item}
                                            type="quick_link"
                                            onEdit={(target) => setEditState({ type: 'quick_link', item: target })}
                                            onToggleVisible={(target) => updateItem('quickLinks', target, { visibleToAgents: target.visibleToAgents === false })}
                                            onArchive={(target) => updateItem('quickLinks', target, { status: 'archived', visibleToAgents: false })}
                                        />
                                    ))}
                                </Stack>
                            </Paper>
                        );
                    })}
                </Stack>
            )}

            {tab === 'storeInfo' && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Store / Office Info Card</Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField label="Store/dealer name" value={config.storeInfo.storeName || ''} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, storeName: event.target.value } }))} fullWidth />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField label="Phone" value={config.storeInfo.phone || ''} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, phone: event.target.value } }))} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Address" value={config.storeInfo.address || ''} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, address: event.target.value } }))} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Google Maps link" value={config.storeInfo.mapUrl || ''} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, mapUrl: event.target.value } }))} fullWidth />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Support/contact note" value={config.storeInfo.supportNote || ''} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, supportNote: event.target.value } }))} fullWidth multiline minRows={2} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel control={<Switch checked={config.storeInfo.visibleToAgents !== false} onChange={(event) => setConfig((previous) => ({ ...previous, storeInfo: { ...previous.storeInfo, visibleToAgents: event.target.checked } }))} />} label="Visible to agents" />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {tab === 'sections' && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>Dashboard Layout Sections</Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Section order controls how the agent dashboard is arranged.
                    </Typography>
                    <Stack spacing={2}>
                        {(config.sections || []).map((section, index) => (
                            <Card variant="outlined" key={section.key}>
                                <CardContent>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} md={5}>
                                            <TextField
                                                label="Section title"
                                                value={section.title || ''}
                                                onChange={(event) => setConfig((previous) => ({
                                                    ...previous,
                                                    sections: previous.sections.map((item, itemIndex) => itemIndex === index ? { ...item, title: event.target.value } : item)
                                                }))}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={3}>
                                            <TextField
                                                label="Sort order"
                                                type="number"
                                                value={section.sortOrder ?? 10}
                                                onChange={(event) => setConfig((previous) => ({
                                                    ...previous,
                                                    sections: previous.sections.map((item, itemIndex) => itemIndex === index ? { ...item, sortOrder: Number(event.target.value) } : item)
                                                }))}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <FormControlLabel
                                                control={<Switch checked={section.visible !== false} onChange={(event) => setConfig((previous) => ({
                                                    ...previous,
                                                    sections: previous.sections.map((item, itemIndex) => itemIndex === index ? { ...item, visible: event.target.checked } : item)
                                                }))} />}
                                                label="Visible on agent dashboard"
                                            />
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        ))}
                    </Stack>
                </Paper>
            )}

            {tab === 'policy' && (
                <Stack spacing={3}>
                    <Alert severity="info">
                        Policy acknowledgement is disabled until you publish a required version. Draft edits are safe and do not block agents.
                    </Alert>
                    {policyError && <Alert severity="error">{policyError}</Alert>}
                    {policyMessage && <Alert severity="success">{policyMessage}</Alert>}

                    {policyLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={3}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">Status</Typography>
                                            <Typography variant="h6">
                                                {policyAdmin?.policy?.enabled ? 'Enabled' : 'Draft only'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Active version {policyAdmin?.policy?.activeVersion || 0}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">Published</Typography>
                                            <Typography variant="body1" fontWeight={700}>
                                                {formatPolicyTimestamp(policyAdmin?.policy?.publishedAt)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Last draft save {formatPolicyTimestamp(policyAdmin?.policy?.updatedAt)}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">Acknowledged</Typography>
                                            <Typography variant="h5" color="success.main">
                                                {policyAdmin?.report?.acknowledgedCount || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                of {policyAdmin?.report?.requiredCount || 0} required agents
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <Card variant="outlined">
                                        <CardContent>
                                            <Typography variant="overline" color="text.secondary">Missing</Typography>
                                            <Typography variant="h5" color={policyAdmin?.report?.missingCount ? 'warning.main' : 'text.primary'}>
                                                {policyAdmin?.report?.missingCount || 0}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Active agents only
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Paper sx={{ p: 3 }}>
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography variant="h6" gutterBottom>
                                            Company Policy & Work Rules Draft
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Save drafts freely. Publishing creates a new required version for active agents.
                                        </Typography>
                                    </Box>
                                    <TextField
                                        label="Policy title"
                                        value={policyDraft.title || ''}
                                        onChange={(event) => setPolicyDraft((previous) => ({ ...previous, title: event.target.value }))}
                                        fullWidth
                                    />
                                    <Editor
                                        apiKey={process.env.REACT_APP_TINYMCE_API_KEY || 'cidq6j1ldl24ah599vsr9xb1rfl9ewlslc2cc1f3xoetd6zn'}
                                        value={policyDraft.html || ''}
                                        onEditorChange={(content) => setPolicyDraft((previous) => ({ ...previous, html: content }))}
                                        init={{
                                            height: 520,
                                            menubar: false,
                                            plugins: [
                                                'advlist', 'autolink', 'lists', 'link', 'charmap',
                                                'anchor', 'searchreplace', 'visualblocks', 'fullscreen',
                                                'insertdatetime', 'table', 'preview', 'help', 'wordcount'
                                            ],
                                            toolbar: 'undo redo | blocks fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link table | preview fullscreen | removeformat help',
                                            content_style: 'body { font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.65; }'
                                        }}
                                    />
                                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                        <Button variant="outlined" onClick={() => setPolicyPreviewOpen(true)} startIcon={<PreviewIcon />}>
                                            Preview
                                        </Button>
                                        <Button variant="outlined" onClick={savePolicyDraft} disabled={policySaving} startIcon={<SaveIcon />}>
                                            {policySaving ? 'Saving...' : 'Save Draft'}
                                        </Button>
                                        <Button variant="contained" onClick={publishPolicy} disabled={policySaving}>
                                            Publish New Required Version
                                        </Button>
                                        {policyAdmin?.policy?.enabled && (
                                            <Button variant="outlined" color="warning" onClick={disablePolicy} disabled={policySaving}>
                                                Disable Requirement
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Active Agent Acknowledgement Report
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Disabled or inactive users are not counted. Publishing a new version resets the required acknowledgement list.
                                </Typography>
                                <Stack spacing={1.25}>
                                    {(policyAdmin?.report?.agents || []).map((agent) => (
                                        <Card variant="outlined" key={agent.userId}>
                                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight={700}>
                                                            {agent.name || agent.username || agent.email || `User ${agent.userId}`}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {agent.email || agent.username || 'No email'}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Chip
                                                            size="small"
                                                            color={agent.acknowledged ? 'success' : (policyAdmin?.policy?.enabled ? 'warning' : 'default')}
                                                            label={agent.acknowledged ? 'Acknowledged' : (policyAdmin?.policy?.enabled ? 'Missing' : 'Not required')}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {agent.acknowledgedAt ? formatPolicyTimestamp(agent.acknowledgedAt) : 'No acknowledgement timestamp'}
                                                        </Typography>
                                                    </Stack>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                    {(policyAdmin?.report?.agents || []).length === 0 && (
                                        <Alert severity="info">No active agents found.</Alert>
                                    )}
                                </Stack>
                            </Paper>
                        </>
                    )}
                </Stack>
            )}

            <ItemEditDialog
                open={Boolean(editState)}
                type={editState?.type}
                item={editState?.item}
                onClose={() => setEditState(null)}
                onSave={(item) => upsertItem(editState?.type === 'announcement' ? 'announcements' : 'quickLinks', item)}
            />

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Preview Agent Dashboard</DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#f4f6f8' }}>
                    {previewDashboard && (
                        <AgentDashboardHome
                            dashboardOverride={previewDashboard}
                            previewMode
                            policyStatusOverride={previewPolicyStatus}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={policyPreviewOpen} onClose={() => setPolicyPreviewOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{policyDraft.title || 'Company Policy & Work Rules'}</DialogTitle>
                <DialogContent dividers>
                    <AgentPolicyContent html={policyDraft.html || ''} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPolicyPreviewOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AgentDashboardManager;
